import { Injectable } from '@angular/core';
import { VetService } from '@app/core/services';
import { AppState } from '@app/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  checkForExistingOwner,
  petOwnerFail,
  placeVetSourceOrder,
  placeVetSourceOrderError,
  placeVetSourceOrderSuccess,
} from '../consultation.actions';
import { catchError, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { Clinic, Consultation, PetInfo, PetOwner } from '@app/core/models';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import { CreatePOPopinComponent, CreatePOPopinData } from '@app/shared/components';
import { translateKey } from '@app/shared/utils/static-helpers/translate';
import { GTMService, mapToolToModule, ShareRecommendationButtonActionGtm, shareRecoTracking } from '@app/core/services/tracking';
import { VetFacade } from '@app/store/vet';
import { ShareRecommendationFacade } from '@app/store/share-recommendation';
import { MatDialog } from '@angular/material/dialog';
import { DialogHelpers } from '@app/shared/utils/static-helpers/dialog-helpers';
import { CoreFacade } from '@app/store/core/core.facade';
import { RcToastTypes } from '@app/core/models/toast';
import { Helper } from '@app/shared/utils';

@Injectable()
export class ConsultationVetSourceEffect {
  public checkForExistingOwner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(checkForExistingOwner),
      switchMap(({ email, owner, clinic, currentConsultation, petInfo }) => {
        if (!owner) {
          return this.dialog
            .open<CreatePOPopinComponent, CreatePOPopinData>(CreatePOPopinComponent, {
              ...DialogHelpers.defaultConfig(),
              data: {
                email,
                country: clinic.companyAddress?.country,
              },
            })
            .afterClosed()
            .pipe(
              switchMap((res) => {
                if (res) {
                  return this.createPetOwner(res?.values).pipe(
                    map(() => placeVetSourceOrder({ email, clinic, currentConsultation, petInfo })),
                  );
                }
                return EMPTY;
              }),
            );
        } else {
          return [placeVetSourceOrder({ email, clinic, petInfo, currentConsultation })];
        }
      }),
    ),
  );

  public placeVetSourceOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(placeVetSourceOrder),
      switchMap(({ clinic, currentConsultation, email, petInfo }) => {
        return this.placeVetSourceOrder(email, clinic, petInfo, currentConsultation).pipe(
          mergeMap(() => this.shareRecommendationTracking()),
          map(() => placeVetSourceOrderSuccess()),
          catchError((error: string) => {
            this.store$.dispatch(placeVetSourceOrderError({ error }));
            return EMPTY;
          }),
        );
      }),
    ),
  );

  public placeVetSourceOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(placeVetSourceOrderSuccess),
        tap(() => {
          return this.coreFacade.setToast({
            id: Helper.randomId(),
            title: '',
            description: translateKey(`request_vet_source_sent`),
            dataQa: 'vs-consultation-vetsource-page-toast-success',
            type: RcToastTypes.Success,
          });
        }),
      ),
    { dispatch: false },
  );

  public placeVetSourceOrderError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(placeVetSourceOrderError),
        tap(({ error }) => {
          return this.coreFacade.setToast({
            id: Helper.randomId(),
            title: translateKey(`error_general_title:`),
            dataQa: 'vs-consultation-vetsource-page-toast-error',
            description: error,
            type: RcToastTypes.Error,
          });
        }),
      ),
    { dispatch: false },
  );

  private readonly currentLanguage$ = this.vetFacade.preferredLanguage$;
  private trackingCode$ = this.vetFacade.conversionConfiguration$.pipe(
    map((conversionConfiguration) => conversionConfiguration?.trackingCode),
  );
  private currentTool$ = this.shareRecommendationFacade.selectShareRecommendationData$.pipe(
    map((shareRecommendationData) => shareRecommendationData.tool),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private vetService: VetService,
    private dialog: MatDialog,
    private readonly vetFacade: VetFacade,
    private readonly shareRecommendationFacade: ShareRecommendationFacade,
    private trackingService: GTMService,
    private readonly coreFacade: CoreFacade,
  ) {}

  private placeVetSourceOrder(
    petOwnerEmail: string,
    clinic: Clinic,
    petInfo: PetInfo,
    currentConsultation: Consultation,
  ): Observable<void> {
    return this.vetService.sendVetsourcesConsultation(petOwnerEmail, clinic, petInfo, currentConsultation);
  }

  private createPetOwner(petOwnerInfo: PetOwner): Observable<PetOwner> {
    return this.vetService.createOwner(petOwnerInfo).pipe(
      catchError((error) => {
        this.store$.dispatch(petOwnerFail({ error }));
        return EMPTY;
      }),
    );
  }

  private shareRecommendationTracking(): Observable<unknown> {
    return combineLatest([this.trackingCode$, this.currentLanguage$, this.currentTool$]).pipe(
      take(1),
      tap(([trackingCode, language, tool]) => {
        this.trackingService.sendInteraction(
          shareRecoTracking(ShareRecommendationButtonActionGtm.SEND_ORDER, mapToolToModule(tool), trackingCode, language),
        );
      }),
    );
  }
}
